import { getNodesBounds } from '@xyflow/react';
import {
    TEMPLATE_PREVIEW_PADDING,
    TEMPLATE_PREVIEW_EXPORT_SCALE,
    TEMPLATE_EXPORT_BACKGROUND,
    TEMPLATE_RENDER_PLACEHOLDER_IMAGE_URLS,
    IMAGE_NODE_WIDTH,
    IMAGE_NODE_HEIGHT,
    GROUP_MIN_WIDTH,
    GROUP_MIN_HEIGHT,
    GROUP_PADDING_X,
    GROUP_PADDING_BOTTOM,
    GROUP_HEADER_HEIGHT,
    TEMPLATE_IMAGE_INPUT_PARAM_KEYS,
} from '../constants/canvas';
import { getFunctionNodeEdgeSlotOptions } from '../constants/edgeInputSlots';
import { cloneValue, createUuid } from './common';
import { getNodeSize, getNodeAbsolutePosition, sortNodesByParentDepth } from './graph';
import { canvasToBlob } from './download';
import { restoreNodeFromProject, restoreEdgesFromProject } from './project';
import { syncConnectedTargetNodes } from './validation';
import { normalizeNodeImageSequences } from './imageSequence';

export const sanitizeTemplateCoverData = (value, fallbackValue = null) => {
    try {
        return JSON.parse(JSON.stringify(value ?? fallbackValue));
    } catch (error) {
        return fallbackValue;
    }
};

export const buildTemplateCoverPayload = ({ groupNodeId, nodes, edges, nodeMap }) => {
    if (!nodes.length) {
        throw new Error('未找到可保存的分组内容');
    }

    const groupNode = nodeMap.get(groupNodeId) || null;
    const bounds = getNodesBounds(nodes);
    const width = Math.max(1, Math.round(bounds.width + TEMPLATE_PREVIEW_PADDING * 2));
    const height = Math.max(1, Math.round(bounds.height + TEMPLATE_PREVIEW_PADDING * 2));

    return {
        schema: 'free-canvas-preview-v1',
        groupNodeId: String(groupNodeId),
        background: TEMPLATE_EXPORT_BACKGROUND,
        width,
        height,
        padding: TEMPLATE_PREVIEW_PADDING,
        bounds: {
            x: Math.round(bounds.x),
            y: Math.round(bounds.y),
            width: Math.round(bounds.width),
            height: Math.round(bounds.height),
        },
        group: groupNode ? {
            id: String(groupNode.id),
            label: groupNode?.data?.label || groupNode?.data?.nodeName || '',
            width: Math.round(getNodeSize(groupNode, GROUP_MIN_WIDTH, GROUP_MIN_HEIGHT).width),
            height: Math.round(getNodeSize(groupNode, GROUP_MIN_WIDTH, GROUP_MIN_HEIGHT).height),
        } : null,
        nodes: nodes.map((node) => {
            const { width: nodeWidth, height: nodeHeight } = getNodeSize(node);

            return {
                id: String(node.id),
                type: node.type,
                position: {
                    x: Math.round(node.position.x - bounds.x + TEMPLATE_PREVIEW_PADDING),
                    y: Math.round(node.position.y - bounds.y + TEMPLATE_PREVIEW_PADDING),
                },
                width: Math.round(nodeWidth),
                height: Math.round(nodeHeight),
                data: sanitizeTemplateCoverData(node.data, {}),
            };
        }),
        edges: edges.map((edge) => ({
            id: String(edge.id),
            type: edge.type,
            source: String(edge.source),
            target: String(edge.target),
            data: sanitizeTemplateCoverData(edge.data, {}),
        })),
    };
};

export const getTemplateNodePreviewImage = (node) => {
    const data = node?.data || {};
    const latestOutput = Array.isArray(data?.latestExecution?.outputs)
        ? data.latestExecution.outputs.find((item) => item?.type === 'image' && item?.url)
        : null;
    const candidates = [
        latestOutput?.url,
        data?.src,
        data?.inputParams?.output,
        data?.inputParams?.srcImage,
    ].filter(Boolean);

    return candidates.find((url) => !TEMPLATE_RENDER_PLACEHOLDER_IMAGE_URLS.has(url)) || '';
};

export const loadCanvasImage = async (url) => {
    if (!url) return null;

    try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) return null;

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        try {
            const image = await new Promise((resolve, reject) => {
                const nextImage = new Image();
                nextImage.onload = () => resolve(nextImage);
                nextImage.onerror = () => reject(new Error('image load failed'));
                nextImage.src = objectUrl;
            });

            return image;
        } finally {
            URL.revokeObjectURL(objectUrl);
        }
    } catch (error) {
        return null;
    }
};

export const drawRoundedRectPath = (context, x, y, width, height, radius) => {
    const nextRadius = Math.min(radius, width / 2, height / 2);
    context.beginPath();
    context.moveTo(x + nextRadius, y);
    context.arcTo(x + width, y, x + width, y + height, nextRadius);
    context.arcTo(x + width, y + height, x, y + height, nextRadius);
    context.arcTo(x, y + height, x, y, nextRadius);
    context.arcTo(x, y, x + width, y, nextRadius);
    context.closePath();
};

export const drawTemplateEdgeLabel = (context, label, centerX, centerY) => {
    if (!label) return;

    const width = 68;
    const height = 24;
    const x = centerX - width / 2;
    const y = centerY - height / 2;

    context.save();
    drawRoundedRectPath(context, x, y, width, height, 12);
    context.fillStyle = 'rgba(255, 255, 255, 0.92)';
    context.fill();
    context.strokeStyle = '#E6EBF2';
    context.lineWidth = 1;
    context.stroke();
    context.fillStyle = '#7A8495';
    context.font = '11px sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(String(label).slice(0, 8), centerX, centerY + 0.5);
    context.restore();
};

export const renderTemplateCoverToFile = async (payload, fileName) => {
    const width = Math.max(1, Math.round((payload?.width || IMAGE_NODE_WIDTH) * TEMPLATE_PREVIEW_EXPORT_SCALE));
    const height = Math.max(1, Math.round((payload?.height || IMAGE_NODE_HEIGHT) * TEMPLATE_PREVIEW_EXPORT_SCALE));
    const scale = TEMPLATE_PREVIEW_EXPORT_SCALE;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    if (!context) {
        throw new Error('模板封面生成失败');
    }

    context.scale(scale, scale);
    context.fillStyle = payload?.background || TEMPLATE_EXPORT_BACKGROUND;
    context.fillRect(0, 0, payload?.width || IMAGE_NODE_WIDTH, payload?.height || IMAGE_NODE_HEIGHT);

    const nodeMap = new Map((payload?.nodes || []).map((node) => [String(node.id), node]));

    (payload?.edges || []).forEach((edge) => {
        const sourceNode = nodeMap.get(String(edge.source));
        const targetNode = nodeMap.get(String(edge.target));
        if (!sourceNode || !targetNode) return;

        const sourceWidth = sourceNode.width || IMAGE_NODE_WIDTH;
        const sourceHeight = sourceNode.height || IMAGE_NODE_HEIGHT;
        const targetHeight = targetNode.height || IMAGE_NODE_HEIGHT;
        const startX = sourceNode.position.x + sourceWidth;
        const startY = sourceNode.position.y + sourceHeight / 2;
        const endX = targetNode.position.x;
        const endY = targetNode.position.y + targetHeight / 2;
        const deltaX = Math.max((endX - startX) * 0.5, 72);
        const label = getFunctionNodeEdgeSlotOptions(targetNode?.data?.functionKey)
            .find((item) => item.key === (edge?.data?.inputSlotKey || ''))?.label || '';

        context.save();
        context.beginPath();
        context.moveTo(startX, startY);
        context.bezierCurveTo(startX + deltaX, startY, endX - deltaX, endY, endX, endY);
        context.strokeStyle = '#D0D7E2';
        context.lineWidth = 2.5;
        context.lineCap = 'round';
        context.stroke();
        context.restore();

        drawTemplateEdgeLabel(context, label, (startX + endX) / 2, (startY + endY) / 2);
    });

    for (const node of payload?.nodes || []) {
        const nodeX = node.position.x;
        const nodeY = node.position.y;
        const nodeWidth = node.width || IMAGE_NODE_WIDTH;
        const nodeHeight = node.height || IMAGE_NODE_HEIGHT;
        const previewImageUrl = getTemplateNodePreviewImage(node);

        context.save();
        context.fillStyle = '#737A84';
        context.font = '12px sans-serif';
        context.textBaseline = 'top';
        context.fillText(
            String(node?.data?.label || node?.data?.imageSequenceLabel || '节点').slice(0, 14),
            nodeX + 8,
            Math.max(0, nodeY - 22),
        );

        drawRoundedRectPath(context, nodeX, nodeY, nodeWidth, nodeHeight, 16);
        context.fillStyle = '#FFFFFF';
        context.fill();
        context.strokeStyle = '#DEE2E8';
        context.lineWidth = 1;
        context.stroke();
        context.restore();

        const image = await loadCanvasImage(previewImageUrl);
        if (image) {
            context.save();
            drawRoundedRectPath(context, nodeX, nodeY, nodeWidth, nodeHeight, 16);
            context.clip();
            context.drawImage(image, nodeX, nodeY, nodeWidth, nodeHeight);
            context.restore();
            continue;
        }

        context.save();
        context.fillStyle = '#FFFFFF';
        context.fillRect(nodeX + 1, nodeY + 1, Math.max(0, nodeWidth - 2), Math.max(0, nodeHeight - 2));
        context.fillStyle = '#737A84';
        context.font = '14px sans-serif';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(node.type === 'imageNode' ? '待上传' : '待生成', nodeX + nodeWidth / 2, nodeY + nodeHeight / 2);
        context.restore();
    }

    const blob = await canvasToBlob(canvas);
    return new File([blob], fileName, { type: 'image/png' });
};

export const sanitizeTemplateInputParamsForCanvas = (inputParams = {}) => {
    if (!inputParams || typeof inputParams !== 'object') return {};

    const nextInputParams = { ...inputParams };

    TEMPLATE_IMAGE_INPUT_PARAM_KEYS.forEach((key) => {
        if (!(key in nextInputParams)) return;
        nextInputParams[key] = key === 'imageUrls' ? [] : '';
    });

    return nextInputParams;
};

export const sanitizeTemplatePanelStateForCanvas = (panelState = {}) => {
    if (!panelState || typeof panelState !== 'object') return {};

    const nextPanelState = { ...panelState };

    if (Array.isArray(nextPanelState.uploads)) {
        nextPanelState.uploads = [];
    } else if (nextPanelState.uploads && typeof nextPanelState.uploads === 'object') {
        nextPanelState.uploads = Object.fromEntries(
            Object.keys(nextPanelState.uploads).map((key) => [key, '']),
        );
    }

    if (Array.isArray(nextPanelState.manualUploads)) {
        nextPanelState.manualUploads = [];
    }

    if (Array.isArray(nextPanelState.linkedUploads)) {
        nextPanelState.linkedUploads = [];
    }

    if (
        nextPanelState.linkedSlotSources
        && typeof nextPanelState.linkedSlotSources === 'object'
        && !Array.isArray(nextPanelState.linkedSlotSources)
    ) {
        nextPanelState.linkedSlotSources = Object.fromEntries(
            Object.entries(nextPanelState.linkedSlotSources).map(([slotKey, value]) => [
                slotKey,
                value && typeof value === 'object'
                    ? {
                        ...value,
                        url: '',
                    }
                    : value,
            ]),
        );
    }

    return nextPanelState;
};

export const sanitizeTemplateNodeForCanvas = (node) => {
    if (!node || node.type === 'groupNode') return node;

    const nextInputParams = sanitizeTemplateInputParamsForCanvas(node.data?.inputParams || {});
    const nextPanelState = sanitizeTemplatePanelStateForCanvas(node.data?.panelState || {});

    if (node.type === 'imageNode') {
        return {
            ...node,
            data: {
                ...node.data,
                src: '',
                inputParams: nextInputParams,
                panelState: nextPanelState,
                latestExecution: null,
                executionStatus: 'idle',
                selectedOutputId: '',
            },
        };
    }

    return {
        ...node,
        data: {
            ...node.data,
            inputParams: nextInputParams,
            panelState: nextPanelState,
            latestExecution: null,
            executionStatus: 'idle',
            selectedOutputId: '',
            displayMode: 'empty',
        },
    };
};

export const stripGroupedClipboardExecution = (clipboard) => {
    if (!clipboard?.nodes?.length) return clipboard;

    const containsGroupNode = clipboard.nodes.some((node) => node?.type === 'groupNode');
    if (!containsGroupNode) return clipboard;

    return {
        ...clipboard,
        nodes: clipboard.nodes.map((node) => {
            if (node?.type === 'groupNode') return node;

            return {
                ...node,
                data: {
                    ...node.data,
                    activeExecutionId: '',
                    groupExecutionLocked: false,
                },
            };
        }),
    };
};

export const wrapTemplateNodesIntoGroup = (nodes, templateName = '') => {
    const templateNodes = (nodes || []).filter(Boolean).map((node) => ({
        ...cloneValue(node),
        selected: false,
    }));
    if (!templateNodes.length) return [];

    const detachedNodes = templateNodes.map((node) => ({
        ...node,
        parentId: undefined,
        extent: undefined,
        expandParent: undefined,
    }));
    const detachedNodeMap = new Map(detachedNodes.map((node) => [node.id, node]));
    const bounds = detachedNodes.reduce((acc, node) => {
        const absolutePosition = getNodeAbsolutePosition(node, detachedNodeMap);
        const { width, height } = getNodeSize(node);

        return {
            minX: Math.min(acc.minX, absolutePosition.x),
            minY: Math.min(acc.minY, absolutePosition.y),
            maxX: Math.max(acc.maxX, absolutePosition.x + width),
            maxY: Math.max(acc.maxY, absolutePosition.y + height),
        };
    }, {
        minX: Infinity,
        minY: Infinity,
        maxX: -Infinity,
        maxY: -Infinity,
    });

    const groupId = `template-group-${createUuid()}`;
    const groupPosition = {
        x: bounds.minX - GROUP_PADDING_X,
        y: bounds.minY - GROUP_HEADER_HEIGHT,
    };
    const groupWidth = Math.max(GROUP_MIN_WIDTH, bounds.maxX - bounds.minX + (GROUP_PADDING_X * 2));
    const groupHeight = Math.max(GROUP_MIN_HEIGHT, bounds.maxY - bounds.minY + GROUP_HEADER_HEIGHT + GROUP_PADDING_BOTTOM);
    const groupLabel = String(templateName || '').trim() || `组合${Date.now()}`;

    const groupNode = {
        id: groupId,
        type: 'groupNode',
        position: groupPosition,
        width: groupWidth,
        height: groupHeight,
        style: {
            width: groupWidth,
            height: groupHeight,
        },
        data: {
            label: groupLabel,
        },
        selected: false,
    };

    const childNodes = detachedNodes.map((node) => {
        const absolutePosition = getNodeAbsolutePosition(node, detachedNodeMap);

        return {
            ...node,
            parentId: groupId,
            extent: 'parent',
            expandParent: true,
            position: {
                x: absolutePosition.x - groupPosition.x,
                y: absolutePosition.y - groupPosition.y,
            },
        };
    });

    return sortNodesByParentDepth([groupNode, ...childNodes]);
};

export const buildTemplateClipboardFromDetail = (templateDetail) => {
    const sourceNodes = Array.isArray(templateDetail?.nodes) ? templateDetail.nodes : [];
    const sourceEdges = Array.isArray(templateDetail?.edges) ? templateDetail.edges : [];
    if (!sourceNodes.length) {
        throw new Error('模板中没有可用节点');
    }

    const serverNodeMap = new Map(sourceNodes.map((node) => [String(node?.id ?? ''), node]));
    const restoredBaseNodes = normalizeNodeImageSequences(sortNodesByParentDepth(
        sourceNodes.map((node) => sanitizeTemplateNodeForCanvas(restoreNodeFromProject(node))),
    ));
    const restoredEdges = restoreEdgesFromProject(sourceEdges, serverNodeMap);
    const syncedNodes = syncConnectedTargetNodes(restoredBaseNodes, restoredEdges)
        .map((node) => ({ ...node, selected: false }));
    const wrappedNodes = syncedNodes.some((node) => node?.type === 'groupNode')
        ? syncedNodes
        : wrapTemplateNodesIntoGroup(syncedNodes, templateDetail?.templateName || '');

    return stripGroupedClipboardExecution({
        nodes: wrappedNodes,
        edges: restoredEdges.map((edge) => ({ ...edge, selected: false })),
    });
};